import { useTranslation } from 'react-i18next'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

type Sentiment = 'all' | 'bullish' | 'neutral' | 'bearish'

interface SentimentFilterProps {
  value: Sentiment
  onChange: (sentiment: Sentiment) => void
}

export function SentimentFilter({ value, onChange }: SentimentFilterProps) {
  const { t } = useTranslation()
  const filters: {
    value: Sentiment
    label: string
    icon?: typeof TrendingUp
    color: string
    activeColor: string
  }[] = [
    {
      value: 'all',
      label: t('all'),
      color: 'text-neutral-400',
      activeColor: 'bg-neutral-800 text-white border-white/[0.15]',
    },
    {
      value: 'bullish',
      label: t('bullish'),
      icon: TrendingUp,
      color: 'text-emerald-400',
      activeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    },
    {
      value: 'neutral',
      label: t('neutral'),
      icon: Minus,
      color: 'text-neutral-400',
      activeColor: 'bg-neutral-500/20 text-neutral-300 border-neutral-500/50',
    },
    {
      value: 'bearish',
      label: t('bearish'),
      icon: TrendingDown,
      color: 'text-red-400',
      activeColor: 'bg-red-500/20 text-red-400 border-red-500/50',
    },
  ]

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
      {filters.map((filter) => {
        const Icon = filter.icon
        const isActive = value === filter.value

        return (
          <button
            key={filter.value}
            onClick={() => onChange(filter.value)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all whitespace-nowrap',
              'hover:scale-105 active:scale-95',
              isActive
                ? filter.activeColor
                : 'bg-neutral-900/50 border-white/[0.08] text-neutral-400 hover:bg-neutral-800 hover:border-white/[0.12]'
            )}
          >
            {Icon && <Icon className="w-4 h-4" />}
            <span className="text-sm font-medium">{filter.label}</span>
          </button>
        )
      })}
    </div>
  )
}
